import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import MuiTooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import {
  ArrowUpFromLine,
  CircleCheck,
  ClipboardCheck,
  Clock3,
  Droplets,
  FileWarning,
  Flame,
  Gauge,
  HardHat,
  HeartPulse,
  History,
  Package,
  RefreshCw,
  ShieldCheck,
  ShieldAlert,
  TriangleAlert,
  UserRound,
  X,
} from "lucide-react";
import "./FieldTechnicianWelcome.css";
import axiosInstance from "../axiosinstance";

const STATUS_COLORS = {
  "Completed": "#1f9d60",
  "In Progress": "#f2b01e",
  "Not Started": "#d64545",
};

const EQUIPMENT_BAR_RED_SCALE = [
  "#991b1b",
  "#b91c1c",
  "#dc2626",
  "#ef4444",
  "#fca5a5",
  "#fee2e2",
];

const TBT_TOPIC_ICONS = {
  confined_space_safety: ShieldCheck,
  hotwork_safety: Flame,
  material_handling: Package,
  work_at_height: ArrowUpFromLine,
  hydrotest_hazard: Droplets,
  psv_handling: Gauge,
};

const SAFETY_RECORD_ICONS = {
  near_miss: TriangleAlert,
  first_aid: HeartPulse,
  lti: ShieldAlert,
  fire_incidents: Flame,
  audits_conducted: ClipboardCheck,
  incident_reports: FileWarning,
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Dashboard metrics are cached per project to avoid refetching on every
// project switch, but the cache must expire so a supervisor watching the
// board eventually sees fresh KPIs without a full page reload. Entries older
// than this are treated as stale and refetched.
const METRICS_CACHE_TTL_MS = 60 * 1000;

const getSelectedProjectId = () => {
  const rawValue = localStorage.getItem("selectedProject");
  if (!rawValue) return null;

  try {
    const parsed = JSON.parse(rawValue);
    return parsed?.id ?? parsed ?? null;
  } catch (error) {
    return null;
  }
};

const formatCount = (value) => toNumber(value).toLocaleString("en-IN");

const formatCompletion = (value) => `${toNumber(value).toFixed(1)}%`;

const clampPercent = (value) => Math.min(100, Math.max(0, toNumber(value)));

const getProgressTone = (value) => {
  if (value >= 80) return "high";
  if (value >= 40) return "medium";
  return "low";
};

const formatGroupLabel = (label) => {
  const text = String(label || "");
  return text.length > 20 ? `${text.slice(0, 20)}...` : text;
};

const getEquipmentBarColor = (index, totalBars) => {
  if (totalBars <= 1) {
    return EQUIPMENT_BAR_RED_SCALE[0];
  }

  const maxScaleIndex = EQUIPMENT_BAR_RED_SCALE.length - 1;
  const paletteIndex = Math.round((index * maxScaleIndex) / (totalBars - 1));
  return EQUIPMENT_BAR_RED_SCALE[paletteIndex];
};

const formatScurveDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short" }).format(date);
};

const formatScurveTooltipDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatTbtHistoryMonth = (value) => {
  if (!value) return "";
  const date = new Date(`${value}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-IN", { month: "long", year: "numeric" }).format(date);
};

const ACTIVITY_TYPE_COLORS = {
  process_started: "#d9822b",
  process_completed: "#1f9d60",
  flange_created: "#b91c1c",
  default: "#7a8794",
};

const ACTIVITY_STATUS_COLORS = {
  Completed: "activity-status-completed",
  "Not Started": "activity-status-not-started",
  Retesting: "activity-status-retesting",
  "In Progress": "activity-status-in-progress",
};

const formatActivityDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

// Render schedule delay (in hours, as computed by the backend) as a compact
// human label, e.g. "3d 4h late" / "5h late". Falls back to the raw status when
// no numeric magnitude is available.
const formatScheduleDelay = (hours, status) => {
  const value = Number(hours);
  if (!Number.isFinite(value) || value <= 0) {
    return status || "Delayed";
  }
  const totalHours = Math.round(value);
  const days = Math.floor(totalHours / 24);
  const remHours = totalHours % 24;
  if (days > 0) {
    return remHours > 0 ? `${days}d ${remHours}h late` : `${days}d late`;
  }
  return `${remHours}h late`;
};

// Human-readable planned start, e.g. "01 Jun 2026, 8:00 AM". Falls back to the
// raw value when it can't be parsed.
const formatPlannedStart = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

// Compact label for how long an equipment is overdue to start.
const formatOverdue = (days) => {
  const value = Number(days);
  if (!Number.isFinite(value) || value <= 0) {
    return "Due today";
  }
  return `${Math.round(value)}d overdue`;
};

const isDev = process.env.NODE_ENV !== "production";

const sanitizeFileNamePart = (value) => {
  const raw = String(value || "").trim();
  const sanitized = raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return sanitized || "project";
};

const isLikelyUuid = (value) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || "").trim(),
  );

const getDashboardProjectName = () => {
  const selectedProjectName = localStorage.getItem("selectedProjectName");
  if (selectedProjectName && selectedProjectName.trim()) {
    return sanitizeFileNamePart(selectedProjectName);
  }

  const selectedProjectRaw = localStorage.getItem("selectedProject");
  if (!selectedProjectRaw) return "project";

  try {
    const parsed = JSON.parse(selectedProjectRaw);
    if (typeof parsed === "string" && parsed.trim()) {
      if (!isLikelyUuid(parsed)) {
        return sanitizeFileNamePart(parsed);
      }
      return "project";
    }
    if (parsed && typeof parsed === "object") {
      const candidate =
        parsed.project_name ||
        parsed.projectName ||
        parsed.name ||
        parsed.title ||
        parsed.label;

      if (candidate && String(candidate).trim()) {
        return sanitizeFileNamePart(candidate);
      }
    }
  } catch (error) {
    // selectedProject can be non-JSON string; ignore parse failure.
  }

  if (!isLikelyUuid(selectedProjectRaw) && selectedProjectRaw.trim()) {
    return sanitizeFileNamePart(selectedProjectRaw);
  }

  return "project";
};

// Brand logo shown in the PDF header (same asset the sidebar uses).
const PDF_LOGO_SRC = "/jecon.png";

// Load an image element for jsPDF.addImage. Resolves null on failure so a
// missing/broken logo never blocks the export.
const loadImageElement = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

const formatPdfTimestamp = (date) =>
  new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);

const FieldTechnicianWelcome = ({ isSidebarExpanded }) => {
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState(() => getSelectedProjectId());
  const [metrics, setMetrics] = useState(null);
  const [tbtTopics, setTbtTopics] = useState([]);
  const [tbtSummaryDate, setTbtSummaryDate] = useState("");
  const [tbtLoading, setTbtLoading] = useState(false);
  const [tbtError, setTbtError] = useState("");
  const [tbtHistoryOpen, setTbtHistoryOpen] = useState(false);
  const [tbtHistory, setTbtHistory] = useState([]);
  const [tbtHistoryMonths, setTbtHistoryMonths] = useState([]);
  const [tbtHistoryMonth, setTbtHistoryMonth] = useState("");
  const [tbtHistoryPage, setTbtHistoryPage] = useState(1);
  const [tbtHistoryHasMore, setTbtHistoryHasMore] = useState(false);
  const [tbtHistoryLoading, setTbtHistoryLoading] = useState(false);
  const [tbtHistoryLoadingMore, setTbtHistoryLoadingMore] = useState(false);
  const [tbtHistoryError, setTbtHistoryError] = useState("");
  const [safetyRecords, setSafetyRecords] = useState([]);
  const [safetyLoading, setSafetyLoading] = useState(false);
  const [safetyError, setSafetyError] = useState("");
  const [manpowerCategories, setManpowerCategories] = useState([]);
  const [manpowerLoading, setManpowerLoading] = useState(false);
  const [manpowerError, setManpowerError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [groupProgressView, setGroupProgressView] = useState("equipment");
  const metricsCacheRef = useRef(new Map());
  const scurveChartRef = useRef(null);
  const dashboardReportRef = useRef(null);
  const pdfExportLockRef = useRef(false);
  const [scurveChartWidth, setScurveChartWidth] = useState(760);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [pdfStatusMessage, setPdfStatusMessage] = useState("");
  const [pdfProgress, setPdfProgress] = useState({ percent: 0, label: "" });

  useEffect(() => {
    // FCM registration is handled globally for all authenticated users in App.js
    // (see registerFcmToken). This component only listens for service-worker
    // call-log messages.
    const handleWorkerMessage = (event) => {
      if (event.data?.type === "NEW_CALL_LOG") {
        const payload = event.data.payload;
        const rawJsonStr = payload?.data?.json;

        if (!rawJsonStr) return;

        try {
          const parsedData = JSON.parse(rawJsonStr);
          const callLog = parsedData.call_log;
          if (!callLog) return;

          const evt = new CustomEvent("new-call-received", {
            detail: callLog,
          });
          window.dispatchEvent(evt);
        } catch (parseError) {
          console.error("[SW] Failed to parse call log JSON:", parseError);
        }
      }
    };

    navigator.serviceWorker?.addEventListener("message", handleWorkerMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener("message", handleWorkerMessage);
    };
  }, []);

  useEffect(() => {
    const syncSelectedProject = (event) => {
      const projectFromEvent = event?.detail?.project_id;
      const latestProject = projectFromEvent ?? getSelectedProjectId();
      setSelectedProject((prev) => (prev === latestProject ? prev : latestProject));
    };

    window.addEventListener("projectChanged", syncSelectedProject);
    window.addEventListener("storage", syncSelectedProject);

    return () => {
      window.removeEventListener("projectChanged", syncSelectedProject);
      window.removeEventListener("storage", syncSelectedProject);
    };
  }, []);

  // Single fetch path shared by the project-change effect and the manual
  // refresh button. `force` bypasses the TTL cache so a user can always pull
  // fresh data on demand. Returns a cleanup callback for the calling effect.
  const loadMetrics = useCallback(
    (projectId, { force = false } = {}) => {
      if (!projectId) return undefined;

      const cached = metricsCacheRef.current.get(projectId);
      const isFresh =
        cached && Date.now() - cached.fetchedAt < METRICS_CACHE_TTL_MS;

      if (!force && isFresh) {
        setMetrics(cached.data);
        setError("");
        setLoading(false);
        return undefined;
      }

      let isMounted = true;
      const fetchMetrics = async () => {
        setLoading(true);
        setError("");

        try {
          const response = await axiosInstance.get(
            `/kpis/project/${projectId}/metrics`,
          );

          const payload = response?.data || {};
          if (!isMounted) return;

          metricsCacheRef.current.set(projectId, {
            data: payload,
            fetchedAt: Date.now(),
          });
          setMetrics(payload);
        } catch (metricsError) {
          if (!isMounted) return;
          console.error("Failed to load dashboard metrics:", metricsError);
          setMetrics(null);
          setError("Unable to load dashboard metrics for the selected project.");
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      };

      fetchMetrics();
      return () => {
        isMounted = false;
      };
    },
    [],
  );

  const loadTbtSummary = useCallback((projectId) => {
    if (!projectId) {
      setTbtTopics([]);
      setTbtSummaryDate("");
      setTbtError("");
      setTbtLoading(false);
      return undefined;
    }

    let isMounted = true;
    setTbtLoading(true);
    setTbtError("");
    axiosInstance.get(`/api/projects/${projectId}/tbt-daily/latest`)
      .then((response) => {
        if (!isMounted) return;
        const topics = Array.isArray(response?.data?.topics) ? response.data.topics : [];
        setTbtTopics(topics);
        setTbtSummaryDate(response?.data?.summary_date || "");
      })
      .catch((requestError) => {
        if (!isMounted) return;
        console.error("Failed to load TBT daily summary:", requestError);
        setTbtTopics([]);
        setTbtSummaryDate("");
        setTbtError("TBT summary is unavailable.");
      })
      .finally(() => {
        if (isMounted) setTbtLoading(false);
      });

    return () => { isMounted = false; };
  }, []);

  const loadTbtHistory = useCallback(({ month = "", page = 1, append = false } = {}) => {
    if (!selectedProject) return;

    if (append) setTbtHistoryLoadingMore(true);
    else setTbtHistoryLoading(true);
    setTbtHistoryError("");
    axiosInstance.get(`/api/projects/${selectedProject}/tbt-daily/history`, { params: { ...(month ? { month } : {}), page, per_page: 10 } })
      .then((response) => {
        const sessions = Array.isArray(response?.data?.sessions) ? response.data.sessions : [];
        setTbtHistory((current) => append ? [...current, ...sessions] : sessions);
        setTbtHistoryMonths(Array.isArray(response?.data?.available_months) ? response.data.available_months : []);
        setTbtHistoryMonth(response?.data?.month || month);
        setTbtHistoryPage(response?.data?.page || page);
        setTbtHistoryHasMore(response?.data?.has_more === true);
      })
      .catch((requestError) => {
        console.error("Failed to load TBT history:", requestError);
        if (!append) setTbtHistory([]);
        setTbtHistoryError("TBT history is unavailable.");
      })
      .finally(() => { setTbtHistoryLoading(false); setTbtHistoryLoadingMore(false); });
  }, [selectedProject]);

  const openTbtHistory = useCallback(() => {
    if (!selectedProject) return;
    setTbtHistoryOpen(true);
    setTbtHistory([]);
    setTbtHistoryMonth("");
    loadTbtHistory();
  }, [selectedProject, loadTbtHistory]);

  const loadSafetySummary = useCallback((projectId) => {
    if (!projectId) {
      setSafetyRecords([]);
      setSafetyError("");
      setSafetyLoading(false);
      return undefined;
    }

    let isMounted = true;
    setSafetyLoading(true);
    setSafetyError("");
    axiosInstance.get(`/api/projects/${projectId}/safety-summary`)
      .then((response) => {
        if (!isMounted) return;
        setSafetyRecords(Array.isArray(response?.data?.records) ? response.data.records : []);
      })
      .catch((requestError) => {
        if (!isMounted) return;
        console.error("Failed to load safety summary:", requestError);
        setSafetyRecords([]);
        setSafetyError("Safety Records are unavailable.");
      })
      .finally(() => {
        if (isMounted) setSafetyLoading(false);
      });

    return () => { isMounted = false; };
  }, []);

  const loadManpowerSummary = useCallback((projectId) => {
    if (!projectId) {
      setManpowerCategories([]);
      setManpowerError("");
      setManpowerLoading(false);
      return undefined;
    }

    let isMounted = true;
    setManpowerLoading(true);
    setManpowerError("");
    axiosInstance.get(`/api/projects/${projectId}/manpower-daily`)
      .then((response) => {
        if (!isMounted) return;
        const categories = Array.isArray(response?.data?.categories) ? response.data.categories : [];
        setManpowerCategories(categories);
      })
      .catch((requestError) => {
        if (!isMounted) return;
        console.error("Failed to load manpower daily summary:", requestError);
        setManpowerCategories([]);
        setManpowerError("Manpower report is unavailable.");
      })
      .finally(() => {
        if (isMounted) setManpowerLoading(false);
      });

    return () => { isMounted = false; };
  }, []);

  const isDashboardRefreshing = loading || tbtLoading || safetyLoading || manpowerLoading;

  const handleRefreshDashboard = useCallback(() => {
    if (!selectedProject || isDashboardRefreshing) return;

    loadMetrics(selectedProject, { force: true });
    loadTbtSummary(selectedProject);
    loadSafetySummary(selectedProject);
    loadManpowerSummary(selectedProject);
  }, [
    selectedProject,
    isDashboardRefreshing,
    loadMetrics,
    loadTbtSummary,
    loadSafetySummary,
    loadManpowerSummary,
  ]);

  useEffect(() => {
    if (!selectedProject) {
      setMetrics(null);
      setError("");
      setLoading(false);
      return undefined;
    }

    return loadMetrics(selectedProject);
  }, [selectedProject, loadMetrics]);

  useEffect(() => loadTbtSummary(selectedProject), [selectedProject, loadTbtSummary]);
  useEffect(() => loadSafetySummary(selectedProject), [selectedProject, loadSafetySummary]);
  useEffect(() => loadManpowerSummary(selectedProject), [selectedProject, loadManpowerSummary]);

  // Let other parts of the app (e.g. completing a process) signal that the
  // cached dashboard is stale so it refetches instead of showing frozen KPIs.
  useEffect(() => {
    const invalidate = () => {
      if (!selectedProject) return;
      metricsCacheRef.current.delete(selectedProject);
      loadMetrics(selectedProject, { force: true });
      loadTbtSummary(selectedProject);
      loadSafetySummary(selectedProject);
      loadManpowerSummary(selectedProject);
    };

    window.addEventListener("dashboardMetricsStale", invalidate);
    return () => {
      window.removeEventListener("dashboardMetricsStale", invalidate);
    };
  }, [selectedProject, loadMetrics, loadTbtSummary, loadSafetySummary, loadManpowerSummary]);

  const totalManpower = useMemo(
    () => manpowerCategories.reduce((total, category) => total + toNumber(category?.headcount), 0),
    [manpowerCategories],
  );

  const summaryCards = useMemo(
    () => [
      {
        label: "Total Equipments",
        value: formatCount(metrics?.total_equipments),
        tone: "blue",
      },
      {
        label: "Total Flanges",
        value: formatCount(metrics?.total_flanges),
        tone: "teal",
      },
      {
        label: "Total Processes",
        value: formatCount(metrics?.total_processes),
        tone: "violet",
      },
      {
        label: "Milestone Processes",
        value: formatCount(metrics?.milestone_processes),
        tone: "amber",
      },
      {
        label: "Completed Processes",
        value: formatCount(metrics?.completed_processes),
        tone: "green",
      },
      {
        // Distinct from the group table's "Progress" column, which is
        // (completed + in progress) / total. This card is strictly
        // completed / non-milestone, so it's labelled "Completed %".
        label: "Completed %",
        value: formatCompletion(metrics?.completion_percentage),
        tone: "amber",
      },
      {
        label: "Equipment On Hold",
        value: formatCount(metrics?.on_hold_equipments),
        tone: "hold",
        href: "/equipments?hold_status=on_hold",
      },
    ],
    [metrics],
  );

  const processStatusData = useMemo(
    () => [
      { name: "Completed", value: toNumber(metrics?.completed_processes) },
      { name: "In Progress", value: toNumber(metrics?.in_progress_processes) },
      { name: "Not Started", value: toNumber(metrics?.not_started_processes) },
    ],
    [metrics],
  );

  const hasProcessData = useMemo(
    () => processStatusData.some((item) => item.value > 0),
    [processStatusData],
  );


  const equipmentGroupProgressRows = useMemo(() => {
    const source = Array.isArray(metrics?.equipment_group_summary)
      ? metrics.equipment_group_summary
      : [];

    return source.map((item) => {
      const processKpi = item?.process_kpi || {};
      const equipmentKpi = item?.equipment_kpi || {};
      const selectedKpi =
        groupProgressView === "equipment" ? equipmentKpi : processKpi;
      const progressValue = clampPercent(
        selectedKpi?.progress_percent ??
          processKpi?.progress_percent ??
          item?.progress_percent,
      );
      const activityValue = toNumber(
        selectedKpi?.scope ??
          processKpi?.scope ??
          item?.total_processes ??
          item?.scope,
      );
      const completedValue = toNumber(
        selectedKpi?.completed ?? processKpi?.completed ?? item?.completed,
      );
      const inProgressValue = toNumber(
        selectedKpi?.in_progress ?? processKpi?.in_progress ?? item?.in_progress,
      );
      const processScurve = Array.isArray(processKpi?.process_scurve)
        ? processKpi.process_scurve
        : Array.isArray(item?.scurve)
          ? item.scurve
          : [];
      const equipmentScurve = Array.isArray(equipmentKpi?.equipment_scurve)
        ? equipmentKpi.equipment_scurve
        : [];
      const selectedScurve =
        groupProgressView === "equipment" ? equipmentScurve : processScurve;
      // The backend falls back to the latest day that had activity when there's
      // no scurve point exactly for today, so this figure can be days/weeks old.
      // Carry its date so the UI can say "as of <date>" instead of implying it
      // is current.
      const todayScurve = selectedScurve.length > 0 ? selectedScurve[0] : null;
      const actualPercentValue = toNumber(todayScurve?.actual_percent ?? 0);
      const plannedPercentValue = toNumber(todayScurve?.planned_percent ?? 0);
      const showActualPlanned = Boolean(todayScurve);

      return {
        category: item?.equipment_group_name || "Unassigned",
        scope: activityValue,
        completed: completedValue,
        inProgress: inProgressValue,
        actualPercent: showActualPlanned ? actualPercentValue : null,
        plannedPercent: showActualPlanned ? plannedPercentValue : null,
        asOfDate: todayScurve?.date || null,
        showActualPlanned,
        progressPercent: progressValue,
        progressText: `${progressValue.toFixed(1)}%`,
        actualTone: getProgressTone(actualPercentValue),
        progressTone: getProgressTone(progressValue),
      };
    });
  }, [groupProgressView, metrics]);

  const equipmentGroupBarData = useMemo(() => {
    const source = Array.isArray(metrics?.equipment_group_summary)
      ? metrics.equipment_group_summary
      : [];

    return source
      .map((item) => {
        const processKpi = item?.process_kpi || {};
        const equipmentKpi = item?.equipment_kpi || {};
        const selectedKpi =
          groupProgressView === "equipment" ? equipmentKpi : processKpi;
        const rawScope = selectedKpi?.scope;
        // Distinguish a genuine 0 from a missing field: the API emits
        // scope on each KPI, so an absent value means "no data" — drop it
        // rather than plotting a misleading 0 bar.
        const hasScope =
          rawScope !== null &&
          rawScope !== undefined &&
          Number.isFinite(Number(rawScope));
        return {
          name: item?.equipment_group_name || "Unassigned",
          count: hasScope ? toNumber(rawScope) : null,
          hasScope,
        };
      })
      .filter((entry) => entry.hasScope)
      .sort((first, second) => second.count - first.count);
  }, [groupProgressView, metrics]);

  const scurveData = useMemo(() => {
    const source = Array.isArray(metrics?.scurve) ? metrics.scurve : [];

    return source
      .map((point) => ({
        date: point?.date || "",
        planned: toNumber(point?.planned_percent ?? 0),
        actual: toNumber(point?.actual_percent ?? 0),
        planned_daily: toNumber(point?.planned_daily ?? 0),
      }))
      .sort((a, b) => String(a.date).localeCompare(String(b.date)));
  }, [metrics]);

  const scurveDisplayData = useMemo(() => {
    if (scurveData.length <= 220) return scurveData;

    const targetPoints = 160;
    const step = Math.ceil(scurveData.length / targetPoints);

    return scurveData.filter((_, index) =>
      index === 0 || index === scurveData.length - 1 || index % step === 0,
    );
  }, [scurveData]);

  const scurveTickDates = useMemo(() => {
    if (scurveDisplayData.length === 0) return [];

    const tickStep =
      scurveDisplayData.length <= 15 ? 1 :
      scurveDisplayData.length <= 30 ? 2 :
      scurveDisplayData.length <= 60 ? 4 :
      scurveDisplayData.length <= 120 ? 6 : 10;

    const ticks = scurveDisplayData
      .filter((_, index) => index % tickStep === 0)
      .map((point) => point.date);

    const firstTick = scurveDisplayData[0]?.date;
    const lastTick = scurveDisplayData[scurveDisplayData.length - 1]?.date;
    if (firstTick && ticks[0] !== firstTick) ticks.unshift(firstTick);
    if (lastTick && ticks[ticks.length - 1] !== lastTick) ticks.push(lastTick);

    return ticks;
  }, [scurveDisplayData]);

  const scurveTickAngle = scurveTickDates.length > 12 ? -24 : 0;

  const scurveYAxis = useMemo(() => {
    const peak = scurveDisplayData.reduce(
      (max, point) =>
        Math.max(max, toNumber(point.planned), toNumber(point.actual)),
      0,
    );

    // Completions can exceed planned scope (rework/retesting → actual > 100%).
    // Extend the axis to the next 25% above the peak so the overflow is
    // visible instead of clipping flat at the top. The 100% line is retained
    // as a reference gridline.
    const domainMax = peak > 100 ? Math.ceil(peak / 25) * 25 : 100;
    const ticks = [];
    for (let tick = 0; tick <= domainMax; tick += 25) ticks.push(tick);

    return { domainMax, ticks };
  }, [scurveDisplayData]);

  const recentActivities = useMemo(
    () => (Array.isArray(metrics?.recent_activities) ? metrics.recent_activities : []),
    [metrics],
  );

  const recentlyReleasedEquipments = useMemo(
    () => Array.isArray(metrics?.recently_released_equipments)
      ? metrics.recently_released_equipments
      : [],
    [metrics],
  );

  const topDelayedEquipments = useMemo(
    () =>
      Array.isArray(metrics?.top_delayed_equipments)
        ? metrics.top_delayed_equipments
        : [],
    [metrics],
  );

  const topOverdueEquipments = useMemo(
    () =>
      Array.isArray(metrics?.top_overdue_equipments)
        ? metrics.top_overdue_equipments
        : [],
    [metrics],
  );

  // Navigate to the Equipment page pre-filtered to a status. When an equipment id
  // is given (a row click), it is also highlighted there.
  const openEquipmentsByStatus = useCallback(
    (status, equipmentId) => {
      const params = new URLSearchParams({ status });
      if (equipmentId) {
        params.set("highlightEquipmentId", String(equipmentId));
      }
      navigate(`/equipments?${params.toString()}`);
    },
    [navigate],
  );

  const openDelayedEquipments = useCallback(
    (equipmentId) => openEquipmentsByStatus("delayed", equipmentId),
    [openEquipmentsByStatus],
  );

  const openOverdueEquipments = useCallback(
    (equipmentId) => openEquipmentsByStatus("not_started", equipmentId),
    [openEquipmentsByStatus],
  );

  useEffect(() => {
    const container = scurveChartRef.current;
    if (!container) return undefined;

    const updateWidth = () => {
      const nextWidth = Math.max(640, Math.floor(container.getBoundingClientRect().width) - 8);
      setScurveChartWidth(nextWidth);
    };

    updateWidth();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => updateWidth());
      observer.observe(container);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [selectedProject, loading, error, scurveData.length]);

  const waitForDashboardReady = async () => {
    if (document.fonts?.ready) {
      await document.fonts.ready;
    }

    const images = Array.from(
      dashboardReportRef.current?.querySelectorAll("img") || [],
    );

    await Promise.all(
      images.map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          const done = () => resolve();
          img.addEventListener("load", done, { once: true });
          img.addEventListener("error", done, { once: true });
        });
      }),
    );

    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => setTimeout(resolve, 300));
  };

  const getInvalidExportElements = (root) => {
    if (!root) return [];

    return Array.from(root.querySelectorAll("*"))
      .map((el) => {
        const rect = el.getBoundingClientRect();
        const style = window.getComputedStyle(el);
        const tag = el.tagName;
        const isCanvas = tag === "CANVAS";
        const isImg = tag === "IMG";
        const isSvg = tag === "SVG";
        const intrinsicWidth = isCanvas
          ? el.width
          : isImg
            ? el.naturalWidth
            : isSvg
              ? Number(el.getAttribute("width") || 0)
              : null;
        const intrinsicHeight = isCanvas
          ? el.height
          : isImg
            ? el.naturalHeight
            : isSvg
              ? Number(el.getAttribute("height") || 0)
              : null;
        return {
          tag,
          className: el.className,
          width: rect.width,
          height: rect.height,
          backgroundImage: style.backgroundImage,
          intrinsicWidth,
          intrinsicHeight,
        };
      })
      .filter(
        (item) =>
          (
            item.width === 0 ||
            item.height === 0 ||
            (typeof item.intrinsicWidth === "number" && item.intrinsicWidth <= 0) ||
            (typeof item.intrinsicHeight === "number" && item.intrinsicHeight <= 0)
          ) &&
          (item.backgroundImage !== "none" || ["CANVAS", "IMG", "SVG"].includes(item.tag)),
      );
  };

  const waitForChartsToHaveSize = async (root) => {
    const maxAttempts = 20;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      await new Promise((resolve) => requestAnimationFrame(resolve));

      const chartNodes = Array.from(
        root.querySelectorAll(".recharts-wrapper, .recharts-responsive-container, svg"),
      );

      const invalidCharts = chartNodes.filter((node) => {
        const rect = node.getBoundingClientRect();
        return rect.width <= 0 || rect.height <= 0;
      });

      if (!invalidCharts.length) return;
    }

    const invalidCharts = Array.from(
      root.querySelectorAll(".recharts-wrapper, .recharts-responsive-container, svg"),
    )
      .map((node) => {
        const rect = node.getBoundingClientRect();
        return {
          tag: node.tagName,
          className: node.className?.toString(),
          width: rect.width,
          height: rect.height,
        };
      })
      .filter((item) => item.width <= 0 || item.height <= 0);

    throw new Error(
      `Charts are not ready for PDF export: ${JSON.stringify(invalidCharts)}`,
    );
  };

  const buildFallbackPdf = (message) => {
    const pdf = new jsPDF("p", "mm", "a4");
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, 210, 297, "F");
    pdf.setTextColor(33, 33, 33);
    pdf.setFontSize(16);
    pdf.text("Dashboard Report", 14, 22);
    pdf.setFontSize(12);
    pdf.text(message || "Dashboard data unavailable", 14, 34);

    const projectName = getDashboardProjectName();
    const fileName = `dashboard-report-${projectName}.pdf`;

    pdf.save(fileName);
  };

  const handleDownloadDashboardReport = async () => {
    if (pdfExportLockRef.current || isExportingPdf || loading) return;

    pdfExportLockRef.current = true;
    setIsExportingPdf(true);
    setPdfStatusMessage("");
    setPdfProgress({ percent: 5, label: "Preparing report…" });

    let exportRoot = null;
    try {
      if (!selectedProject) {
        throw new Error("Select a project before exporting.");
      }

      if (!metrics || error) {
        buildFallbackPdf("Dashboard data unavailable");
        setPdfStatusMessage("Dashboard data unavailable. Downloaded fallback PDF.");
        return;
      }

      setPdfProgress({ percent: 15, label: "Waiting for charts to render…" });
      await waitForDashboardReady();
      const reportElement = dashboardReportRef.current;
      if (!reportElement) {
        throw new Error("Dashboard export target not found");
      }

      await waitForChartsToHaveSize(reportElement);

      const targetRect = reportElement.getBoundingClientRect();
      if (targetRect.width <= 0 || targetRect.height <= 0) {
        throw new Error(
          `Dashboard export target has invalid size: ${targetRect.width}x${targetRect.height}`,
        );
      }

      exportRoot = document.createElement("div");
      exportRoot.style.position = "fixed";
      exportRoot.style.left = "-100000px";
      exportRoot.style.top = "0";
      exportRoot.style.width = "1200px";
      exportRoot.style.background = "#ffffff";
      exportRoot.style.padding = "0";
      exportRoot.style.margin = "0";
      exportRoot.style.zIndex = "-1";

      const reportClone = reportElement.cloneNode(true);
      reportClone.classList.add("dashboard-pdf-export");
      reportClone.style.width = "1200px";
      reportClone.style.maxWidth = "1200px";
      reportClone.style.minHeight = "auto";
      reportClone.style.height = "auto";
      reportClone.style.overflow = "visible";
      reportClone.style.paddingBottom = "0";

      exportRoot.appendChild(reportClone);
      document.body.appendChild(exportRoot);

      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => setTimeout(resolve, 300));

      const sectionNodes = Array.from(reportClone.querySelectorAll(":scope > section"));
      if (!sectionNodes.length) {
        throw new Error("No dashboard sections found for export");
      }

      setPdfProgress({ percent: 25, label: "Loading branding…" });
      const logoImg = await loadImageElement(PDF_LOGO_SRC);

      const pdf = new jsPDF("l", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 8;
      // Reserve a top band for the logo/title header and a bottom band for the
      // page-number footer; content is laid out strictly between them.
      const headerHeight = 20;
      const footerHeight = 8;
      const contentTop = headerHeight;
      const contentBottom = pageHeight - footerHeight;
      const contentWidth = pageWidth - margin * 2;
      const maxContentHeight = contentBottom - contentTop;
      const sectionGap = 4;
      let y = contentTop;
      let hasAddedContent = false;

      const addCanvasSlices = (sectionCanvas) => {
        const pxPerMm = sectionCanvas.width / contentWidth;
        const maxSliceHeightPx = Math.max(1, Math.floor(maxContentHeight * pxPerMm));
        let sliceStartY = 0;
        let isFirstSlice = true;

        while (sliceStartY < sectionCanvas.height) {
          const sliceHeightPx = Math.min(maxSliceHeightPx, sectionCanvas.height - sliceStartY);
          const renderHeightMm = sliceHeightPx / pxPerMm;

          if (hasAddedContent && (!isFirstSlice || y + renderHeightMm > contentBottom)) {
            pdf.addPage();
            y = contentTop;
          }

          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = sectionCanvas.width;
          sliceCanvas.height = sliceHeightPx;
          const ctx = sliceCanvas.getContext("2d");
          if (!ctx) return;

          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
          ctx.drawImage(
            sectionCanvas,
            0,
            sliceStartY,
            sectionCanvas.width,
            sliceHeightPx,
            0,
            0,
            sectionCanvas.width,
            sliceHeightPx,
          );

          const imgData = sliceCanvas.toDataURL("image/png", 1.0);
          pdf.addImage(imgData, "PNG", margin, y, contentWidth, renderHeightMm, undefined, "FAST");
          hasAddedContent = true;
          y += renderHeightMm + sectionGap;
          sliceStartY += sliceHeightPx;
          isFirstSlice = false;
        }
      };

      const totalSections = sectionNodes.length;
      for (let index = 0; index < totalSections; index += 1) {
        const sectionNode = sectionNodes[index];
        // Capturing the sections is the slow part — spread 30%→90% across them
        // so the bar advances steadily instead of sitting still.
        setPdfProgress({
          percent: 30 + Math.round(((index + 1) / totalSections) * 60),
          label: `Capturing section ${index + 1} of ${totalSections}…`,
        });

        const rect = sectionNode.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) continue;

        const sectionCanvas = await html2canvas(sectionNode, {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          backgroundColor: "#ffffff",
          logging: isDev,
          imageTimeout: 15000,
          removeContainer: true,
          width: Math.ceil(sectionNode.scrollWidth || rect.width),
          height: Math.ceil(sectionNode.scrollHeight || rect.height),
        });

        if (!sectionCanvas || sectionCanvas.width <= 0 || sectionCanvas.height <= 0) continue;
        addCanvasSlices(sectionCanvas);
      }

      // Brand every page: logo + title header and a page-number footer.
      setPdfProgress({ percent: 94, label: "Adding header & branding…" });
      const generatedAt = formatPdfTimestamp(new Date());
      const totalPages = pdf.getNumberOfPages();

      for (let pageIndex = 1; pageIndex <= totalPages; pageIndex += 1) {
        pdf.setPage(pageIndex);

        if (logoImg && logoImg.width && logoImg.height) {
          const logoH = 12;
          const logoW = (logoImg.width / logoImg.height) * logoH;
          pdf.addImage(logoImg, "PNG", margin, 5, logoW, logoH, undefined, "FAST");
        }

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(13);
        pdf.setTextColor(31, 41, 55);
        pdf.text("Jecon Engineers Pvt. Ltd.", pageWidth - margin, 13, { align: "right" });
        pdf.setDrawColor(225, 229, 235);
        pdf.setLineWidth(0.3);
        pdf.line(margin, headerHeight - 2, pageWidth - margin, headerHeight - 2);

        pdf.line(margin, contentBottom + 2, pageWidth - margin, contentBottom + 2);
        pdf.setFontSize(8);
        pdf.setTextColor(148, 163, 184);
      
      }

      setPdfProgress({ percent: 99, label: "Saving file…" });
      const projectName = getDashboardProjectName();
      const fileName = `dashboard-report-${projectName}.pdf`;
      pdf.save(fileName);
      setPdfProgress({ percent: 100, label: "Download ready" });
    } catch (exportError) {
      console.error("Failed to generate dashboard PDF:", exportError);
      const latestRoot = dashboardReportRef.current;
      if (latestRoot) {
        console.warn(
          "Zero-size export elements on failure",
          getInvalidExportElements(latestRoot),
        );
      }
      const message =
        exportError instanceof Error
          ? exportError.message
          : "Unknown dashboard PDF export error";
      setPdfStatusMessage(`Failed to generate dashboard PDF: ${message}`);
    } finally {
      if (exportRoot?.parentNode) {
        exportRoot.parentNode.removeChild(exportRoot);
      }
      setIsExportingPdf(false);
      setPdfProgress({ percent: 0, label: "" });
      pdfExportLockRef.current = false;
    }
  };

  return (
    <div
      className={`welcome-container ${isSidebarExpanded ? "expanded" : "collapsed"}`}
    >
      <div className="dashboard-content">
        {isExportingPdf && (
          <div className="pdf-export-overlay" role="status" aria-live="polite">
            <div className="pdf-export-modal">
              <img
                src="/favicon.png"
                alt=""
                aria-hidden="true"
                className="pdf-export-logo"
              />
              <p className="pdf-export-title">Preparing your dashboard PDF</p>
              <p className="pdf-export-step">{pdfProgress.label || "Working…"}</p>
              <div
                className="pdf-export-bar"
                role="progressbar"
                aria-valuenow={pdfProgress.percent}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="pdf-export-bar-fill"
                  style={{ width: `${pdfProgress.percent}%` }}
                />
              </div>
              <p className="pdf-export-percent">{pdfProgress.percent}%</p>
              <p className="pdf-export-hint">Please keep this tab open…</p>
            </div>
          </div>
        )}
        <div className="equipment-dashboard-page-header">
          <h1>Equipment Dashboard</h1>
          {selectedProject && (
            <div className="dashboard-actions">
            <MuiTooltip title="Refresh" arrow placement="top">
              <span className="dashboard-icon-btn-wrap">
                <button
                  type="button"
                  className="dashboard-icon-btn"
                  onClick={handleRefreshDashboard}
                  disabled={isDashboardRefreshing}
                  aria-label="Refresh dashboard data"
                >
                  <RefreshCw
                    size={15}
                    strokeWidth={2}
                    className={isDashboardRefreshing ? "refresh-icon-spinning" : ""}
                  />
                </button>
              </span>
            </MuiTooltip>
            <button
              type="button"
              className="dashboard-download-btn"
              onClick={handleDownloadDashboardReport}
              // Block export while loading, on error, or before metrics arrive —
              // otherwise html2canvas captures the empty/fallback layout and
              // silently downloads a blank report.
              disabled={isExportingPdf || loading || Boolean(error) || !metrics}
            >
              {isExportingPdf ? "Preparing PDF..." : "Download Dashboard Report"}
            </button>
            </div>
          )}
        </div>
        {pdfStatusMessage && (
          <div className="dashboard-empty-card error">
            <p>{pdfStatusMessage}</p>
          </div>
        )}
        {/* <div className="dashboard-header">
         
          
        </div> */}

        {!selectedProject && (
          <div className="dashboard-empty-card">
            <h2>Project Required</h2>
            <p>Please select a project from the sidebar dropdown to load dashboard data.</p>
          </div>
        )}

        {selectedProject && loading && (
          <div className="dashboard-empty-card">
            <h2>Loading Dashboard</h2>
            <p>Fetching the latest project KPIs and chart data...</p>
          </div>
        )}

        {selectedProject && !loading && error && (
          <div className="dashboard-empty-card error">
            <h2>Data Unavailable</h2>
            <p>{error}</p>
          </div>
        )}

        {selectedProject && !loading && !error && (
          <div
            ref={dashboardReportRef}
            data-dashboard-export="true"
            className="dashboard-report-area"
          >
            <section className="dashboard-summary-grid">
              {summaryCards.map((card) => (
                <article
                  key={card.label}
                  className={`summary-card tone-${card.tone}${card.href ? " is-clickable" : ""}`}
                  role={card.href ? "link" : undefined}
                  tabIndex={card.href ? 0 : undefined}
                  onClick={card.href ? () => navigate(card.href) : undefined}
                  onKeyDown={card.href ? (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      navigate(card.href);
                    }
                  } : undefined}
                >
                  <p className="summary-label">{card.label}</p>
                  <h3 className="summary-value">{card.value}</h3>
                </article>
              ))}
            </section>

            <section className="dashboard-chart-grid">
              <article className="chart-card chart-card-process">
                <div className="chart-header">
                  <h2>Process Status Overview</h2>
                </div>
                {!hasProcessData ? (
                  <div className="chart-empty-state">No process status data available.</div>
                ) : (
                  <div className="chart-body">
                    {/* <div className="process-donut-canvas process-status-chart-wrapper"> */}
                    <div
                      className="process-donut-canvas process-status-chart-wrapper"
                      style={{ width: "100%", height: 260, minHeight: 260 }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={processStatusData}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={68}
                            outerRadius={100}
                            paddingAngle={2}
                            stroke="#ffffff"
                            strokeWidth={2}
                          >
                            {processStatusData.map((entry) => (
                              <Cell key={entry.name} fill={STATUS_COLORS[entry.name]} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value, name) => [formatCount(value), name]}
                            contentStyle={{
                              borderRadius: 10,
                              border: "1px solid #d8e3f0",
                              boxShadow: "0 6px 20px rgba(28, 55, 90, 0.12)",
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="status-legend" aria-label="Process status legend">
                      {processStatusData.map((item) => (
                        <div key={item.name} className="status-legend-item">
                          <span
                            className="status-dot"
                            style={{ backgroundColor: STATUS_COLORS[item.name] || "#7a8794" }}
                          />
                          <span className="status-legend-label">{item.name}</span>
                        </div>
                      ))}
                    </div>

                  </div>
                )}
              </article>

              <article className="chart-card chart-card-equipment">
                <div className="chart-header">
                  <h2>Equipment Group Summary</h2>
                </div>
                {equipmentGroupBarData.length === 0 ? (
                  <div className="chart-empty-state">No equipment group data available.</div>
                ) : (
                  // <div className="chart-body equipment-group-chart-wrapper">

                  <div
                    className="chart-body equipment-group-chart-wrapper"
                    style={{ width: "100%", height: 320, minHeight: 320 }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={equipmentGroupBarData} margin={{ top: 20, right: 24, left: 8, bottom: 60 }}>
                        <XAxis
                          dataKey="name"
                          interval={0}
                          angle={-18}
                          textAnchor="end"
                          height={65}
                          minTickGap={8}
                          tickFormatter={formatGroupLabel}
                          tick={{ fontSize: 12, fill: "#5f6e7a" }}
                        />
                        <YAxis allowDecimals={false} />
                        <Tooltip
                          formatter={(value) => [
                            formatCount(value),
                            groupProgressView === "equipment"
                              ? "Equipment Scope"
                              : "Activity Scope",
                          ]}
                          labelFormatter={(value) => value}
                          contentStyle={{
                            borderRadius: 10,
                            border: "1px solid #d8e3f0",
                            boxShadow: "0 6px 20px rgba(28, 55, 90, 0.12)",
                          }}
                        />
                        <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                          {equipmentGroupBarData.map((entry, index) => (
                            <Cell
                              key={`${entry.name}-${index}`}
                              fill={getEquipmentBarColor(index, equipmentGroupBarData.length)}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </article>
            </section>

            <section className="dashboard-table-section">
              <article className="chart-card chart-card-scurve">
                <div className="chart-header chart-header-scurve">
                  <div>
                    <h2>S-Curve - Project Progress</h2>
                    <p className="chart-subtitle">Planned vs Actual cumulative progress over time</p>
                  </div>
                  <div className="scurve-legend" aria-label="S-Curve legend">
                    <span className="scurve-legend-item">
                      <span className="scurve-legend-line planned" />
                      <span>Planned</span>
                    </span>
                    <span className="scurve-legend-item">
                      <span className="scurve-legend-line actual" />
                      <span>Actual</span>
                    </span>
                  </div>
                </div>
                {scurveData.length === 0 ? (
                  <div className="chart-empty-state">No S-curve data available</div>
                ) : (
                  <div
                    ref={scurveChartRef}
                    className="chart-body scurve-chart-wrapper"
                    style={{
                      width: "100%",
                      height: 320,
                      background: "#fff",
                      overflow: "hidden",
                      paddingBottom: 4,
                    }}
                  >
                    <ComposedChart
                      width={scurveChartWidth}
                      height={320}
                      data={scurveDisplayData}
                      margin={{ top: 8, right: 20, left: 8, bottom: 18 }}
                    >
                        <defs>
                          <linearGradient id="colorPlanned" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8F1D1D" stopOpacity={0.25} />
                            <stop offset="95%" stopColor="#8F1D1D" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} stroke="#f2e8e8" strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={formatScurveDate}
                          ticks={scurveTickDates}
                          interval={0}
                          angle={scurveTickAngle}
                          textAnchor={scurveTickAngle === 0 ? "middle" : "end"}
                          height={scurveTickAngle === 0 ? 34 : 52}
                          tickMargin={8}
                          minTickGap={18}
                          tick={{ fontSize: 12, fill: "#5f6e7a" }}
                        />
                        <YAxis
                          domain={[0, scurveYAxis.domainMax]}
                          ticks={scurveYAxis.ticks}
                          tickFormatter={(value) => `${toNumber(value).toFixed(0)}%`}
                          tick={{ fontSize: 12, fill: "#5f6e7a" }}
                        />
                        <Tooltip
                          labelFormatter={formatScurveTooltipDate}
                          formatter={(value, name) => [
                            `${toNumber(value).toFixed(1)}%`,
                            name === "planned" ? "Planned" : "Actual",
                          ]}
                          contentStyle={{
                            borderRadius: 10,
                            border: "1px solid #ead8d6",
                            boxShadow: "0 6px 20px rgba(59, 17, 14, 0.10)",
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="planned"
                          stroke="none"
                          fill="url(#colorPlanned)"
                          // This Area is only the visual gradient fill for planned.
                          // Recharts registers graphical items in legend/tooltip by default,
                          // so without these props it duplicates the "Planned" entry.
                          legendType="none"
                          tooltipType="none"
                        />
                        <Line
                          dataKey="planned"
                          stroke="#8F1D1D"
                          strokeWidth={3}
                          dot={false}
                          activeDot={{ r: 4 }}
                          legendType="plainline"
                        />
                        <Line
                          dataKey="actual"
                          stroke="#C46A6A"
                          strokeWidth={2.5}
                          strokeDasharray="6 4"
                          strokeLinecap="round"
                          dot={false}
                          activeDot={{ r: 4, stroke: "#C46A6A", strokeWidth: 2, fill: "#fff" }}
                          legendType="plainline"
                        />
                    </ComposedChart>
                  </div>
                )}
              </article>
            </section>

            <section className="dashboard-table-section">
              <article className="chart-card chart-card-table table-card equipment-group-progress-card">
                <div className="chart-header chart-header-with-toggle">
                  <div>
                    <h2>Equipment Group Progress Summary</h2>
                    <p className="chart-subtitle">Group-wise scope, execution, and progress signals</p>
                  </div>
                  <div className="summary-view-toggle" role="tablist" aria-label="Equipment Group Progress Summary view">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={groupProgressView === "equipment"}
                      className={`summary-view-toggle-btn ${groupProgressView === "equipment" ? "is-active" : ""}`}
                      onClick={() => setGroupProgressView("equipment")}
                    >
                      <PrecisionManufacturingOutlinedIcon fontSize="inherit" />
                      By Equipment
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={groupProgressView === "activity"}
                      className={`summary-view-toggle-btn ${groupProgressView === "activity" ? "is-active" : ""}`}
                      onClick={() => setGroupProgressView("activity")}
                    >
                      <BoltOutlinedIcon fontSize="inherit" />
                      By Activity
                    </button>
                  </div>
                </div>
                {equipmentGroupProgressRows.length === 0 ? (
                  <div className="chart-empty-state">No equipment group data available.</div>
                ) : (
                  <div className="progress-table-wrap equipment-group-progress-wrap">
                    <table className="progress-table equipment-group-progress-table">
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Scope</th>
                          <th>Completed</th>
                          <th>In Progress</th>
                          <th>
                            <div className="progress-header-with-info">
                              <span>Actual / Planned</span>
                              <MuiTooltip
                                title="Cumulative Actual vs Planned progress as of the most recent day with activity (shown per row). This may predate today if there's been no recent activity."
                                arrow
                                placement="top"
                                enterDelay={120}
                                leaveDelay={80}
                                slotProps={{
                                  tooltip: {
                                    sx: {
                                      bgcolor: "#0f172a",
                                      color: "#fff",
                                      fontSize: "11px",
                                      fontWeight: 500,
                                      lineHeight: 1.35,
                                      borderRadius: "8px",
                                      px: 1.2,
                                      py: 0.9,
                                      maxWidth: "220px",
                                      boxShadow:
                                        "0 10px 24px rgba(2, 6, 23, 0.30)",
                                    },
                                  },
                                  arrow: {
                                    sx: {
                                      color: "#0f172a",
                                    },
                                  },
                                  popper: {
                                    sx: { zIndex: 9999 },
                                  },
                                }}
                              >
                                <button
                                  type="button"
                                  className="progress-info-icon-btn"
                                  aria-label="Actual vs Planned information"
                                >
                                  <InfoOutlinedIcon fontSize="inherit" />
                                </button>
                              </MuiTooltip>
                            </div>
                          </th>
                          <th>
                            <div className="progress-header-with-info">
                              <span>Progress</span>
                              <MuiTooltip
                                title="Started work: (Completed + In Progress) ÷ Total. Higher than the Completed % card, which counts only completed work."
                                arrow
                                placement="top"
                                enterDelay={120}
                                leaveDelay={80}
                                slotProps={{
                                  tooltip: {
                                    sx: {
                                      bgcolor: "#0f172a",
                                      color: "#fff",
                                      fontSize: "11px",
                                      fontWeight: 500,
                                      lineHeight: 1.35,
                                      borderRadius: "8px",
                                      px: 1.2,
                                      py: 0.9,
                                      maxWidth: "220px",
                                      boxShadow: "0 10px 24px rgba(2, 6, 23, 0.30)",
                                    },
                                  },
                                  arrow: { sx: { color: "#0f172a" } },
                                  popper: { sx: { zIndex: 9999 } },
                                }}
                              >
                                <button
                                  type="button"
                                  className="progress-info-icon-btn"
                                  aria-label="Progress calculation information"
                                >
                                  <InfoOutlinedIcon fontSize="inherit" />
                                </button>
                              </MuiTooltip>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {equipmentGroupProgressRows.map((row, index) => (
                          <tr key={`${row.category}-${index}`}>
                            <td className="egps-category-cell" title={row.category}>
                              <span className="egps-category-name">{row.category}</span>
                            </td>
                            <td><span className="egps-scope-text">{formatCount(row.scope)}</span></td>
                            <td><span className="egps-completed-text">{formatCount(row.completed)}</span></td>
                            <td><span className="egps-inprogress-text">{formatCount(row.inProgress)}</span></td>
                            <td className="actual-planned-cell">
                              {row.showActualPlanned ? (
                                <div className="actual-planned-stack">
                                  <div className="actual-planned-line">
                                    <span className={`actual-planned-actual ${row.actualTone}`}>
                                      {row.actualPercent.toFixed(1)}%
                                    </span>
                                    <span className="actual-planned-separator">/</span>
                                    <span className="actual-planned-planned">
                                      {row.plannedPercent.toFixed(1)}%
                                    </span>
                                </div>
                                {/* {row.asOfDate && (
                                  <span className="actual-planned-asof">
                                    as of {formatScurveTooltipDate(row.asOfDate)}
                                  </span>
                                )} */}
                                </div>
                              ) : (
                                <div className="actual-planned-line">
                                  <span className="actual-planned-label">N/A</span>
                                </div>
                              )}
                            </td>
                            <td>
                              <div className="progress-cell">
                                <div className="progress-track">
                                  {(() => {
                                    const safeProgressPercent = Number(row.progressPercent) > 0
                                      ? row.progressPercent
                                      : 1;
                                    const hiddenZeroProgress = Number(row.progressPercent) <= 0;
                                    return (
                                  <div
                                    className={`progress-fill ${row.progressTone}`}
                                    style={{
                                      width: `${safeProgressPercent}%`,
                                      opacity: hiddenZeroProgress ? 0 : 1,
                                    }}
                                  />
                                    );
                                  })()}
                                </div>
                                <span className="progress-text">{row.progressText}</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </article>
            </section>

            <section className="dashboard-table-section">
              <div className="dashboard-dual-table-grid risk-grid">
                <article className="chart-card chart-card-table table-card risk-card risk-card-delayed">
                  <div className="risk-card-header">
                    <div className="risk-card-heading">
                      <span className="risk-card-dot risk-dot-delayed" />
                      <div>
                        <h2>Top Delayed Equipments</h2>
                        <p className="chart-subtitle">
                          Equipments whose latest completed process finished after
                          its planned date
                        </p>
                      </div>
                    </div>
                    {topDelayedEquipments.length > 0 && (
                      <button
                        type="button"
                        className="risk-viewall-btn risk-viewall-delayed"
                        onClick={() => openDelayedEquipments()}
                      >
                        View all
                      </button>
                    )}
                  </div>
                  {topDelayedEquipments.length === 0 ? (
                    <div className="chart-empty-state">
                      No delayed completions.
                      {topOverdueEquipments.length > 0
                        ? " See “Overdue to Start” for pending starts."
                        : ""}
                    </div>
                  ) : (
                    <table className="risk-table">
                      <thead>
                        <tr>
                          <th>Equipment</th>
                          <th>Last process</th>
                          <th className="risk-col-right">Delay</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topDelayedEquipments.map((equipment, index) => {
                          const open = () => openDelayedEquipments(equipment?.id);
                          return (
                            <tr
                              key={`${equipment?.id || "delayed"}-${index}`}
                              className="risk-row"
                              role="button"
                              tabIndex={0}
                              onClick={open}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  open();
                                }
                              }}
                              title="View on Equipment page"
                            >
                              <td className="risk-eq-cell">
                                <span className="risk-eq-tag">
                                  {equipment?.equipment_tag_number || "-"}
                                </span>
                                {equipment?.equipment_group && (
                                  <span className="risk-eq-group">
                                    Group {equipment.equipment_group}
                                  </span>
                                )}
                              </td>
                              <td className="risk-process-cell">
                                {equipment?.last_process || "-"}
                              </td>
                              <td className="risk-col-right">
                                <span className="top-delayed-meta">
                                  <span
                                    className="top-risk-dot top-risk-dot-delayed"
                                    aria-hidden="true"
                                  />
                                  {formatScheduleDelay(
                                    equipment?.schedule_delay_hours,
                                    equipment?.schedule_delay_status,
                                  )}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </article>

                <article className="chart-card chart-card-table table-card risk-card risk-card-overdue">
                  <div className="risk-card-header">
                    <div className="risk-card-heading">
                      <span className="risk-card-dot risk-dot-overdue" />
                      <div>
                        <h2>Overdue to Start</h2>
                        <p className="chart-subtitle">
                          Earliest process is past its planned start, but no process
                          has begun yet
                        </p>
                      </div>
                    </div>
                    {topOverdueEquipments.length > 0 && (
                      <button
                        type="button"
                        className="risk-viewall-btn risk-viewall-overdue"
                        onClick={() => openOverdueEquipments()}
                      >
                        View all
                      </button>
                    )}
                  </div>
                  {topOverdueEquipments.length === 0 ? (
                    <div className="chart-empty-state">
                      No equipment is overdue to start.
                    </div>
                  ) : (
                    <table className="risk-table">
                      <thead>
                        <tr>
                          <th>Equipment</th>
                          <th>Planned start</th>
                          <th className="risk-col-right">Overdue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topOverdueEquipments.map((equipment, index) => {
                          const open = () => openOverdueEquipments(equipment?.id);
                          return (
                            <tr
                              key={`${equipment?.id || "overdue"}-${index}`}
                              className="risk-row"
                              role="button"
                              tabIndex={0}
                              onClick={open}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  open();
                                }
                              }}
                              title="View on Equipment page"
                            >
                              <td className="risk-eq-cell">
                                <span className="risk-eq-tag">
                                  {equipment?.equipment_tag_number || "-"}
                                </span>
                                {equipment?.equipment_group && (
                                  <span className="risk-eq-group">
                                    Group {equipment.equipment_group}
                                  </span>
                                )}
                              </td>
                              <td className="risk-date-cell">
                                {formatPlannedStart(equipment?.planned_start)}
                              </td>
                              <td className="risk-col-right">
                                <span className="top-overdue-meta">
                                  <span
                                    className="top-risk-dot top-risk-dot-overdue"
                                    aria-hidden="true"
                                  />
                                  {formatOverdue(equipment?.overdue_days)}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </article>
              </div>
            </section>

            <section className="dashboard-table-section">
              <div className="dashboard-dual-table-grid">
                <article className="chart-card chart-card-table table-card">
                  <div className="chart-header">
                    <div>
                      <h2 className="tbt-dashboard-heading"><HardHat size={18} aria-hidden="true" />Tool Box Talk Summary</h2>
                      <button type="button" className="tbt-history-button" onClick={openTbtHistory} disabled={!selectedProject}>
                        <History size={15} aria-hidden="true" />History
                      </button>
                      <p className="chart-subtitle">{tbtSummaryDate ? `Latest saved TBT — ${tbtSummaryDate}` : "No TBT saved yet"}</p>
                    </div>
                  </div>
                  <div className="simple-table-wrap">
                    <table className="simple-report-table tbt-dashboard-table">
                      <thead>
                        <tr><th>TBT Topic</th></tr>
                      </thead>
                      <tbody>
                        {tbtLoading && (
                          <tr><td colSpan="3" className="tbt-dashboard-message">Loading TBT summary…</td></tr>
                        )}
                        {!tbtLoading && tbtError && (
                          <tr><td colSpan="3" className="tbt-dashboard-message">{tbtError}</td></tr>
                        )}
                        {!tbtLoading && !tbtError && tbtTopics.map((topic) => (
                          <tr key={topic.topic_key}>
                            <td>
                              {(() => {
                                const TopicIcon = TBT_TOPIC_ICONS[topic.topic_key] || HardHat;
                                return <span className="tbt-dashboard-topic"><TopicIcon size={15} aria-hidden="true" />{topic.topic_name}</span>;
                              })()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </article>

                <article className="chart-card chart-card-table table-card">
                  <div className="chart-header">
                    <div>
                      <h2 className="safety-dashboard-heading"><ShieldCheck size={18} aria-hidden="true" />Safety Records</h2>
                      <p className="chart-subtitle">Today&apos;s and cumulative safety record count</p>
                    </div>
                  </div>
                  <div className="simple-table-wrap">
                    <table className="simple-report-table safety-dashboard-table">
                      <thead>
                        <tr><th>Safety Record</th><th>Today</th><th>Cumulative</th></tr>
                      </thead>
                      <tbody>
                        {safetyLoading && (
                          <tr><td colSpan="3" className="safety-dashboard-message">Loading Safety Records…</td></tr>
                        )}
                        {!safetyLoading && safetyError && (
                          <tr><td colSpan="3" className="safety-dashboard-message">{safetyError}</td></tr>
                        )}
                        {!safetyLoading && !safetyError && safetyRecords.map((record) => {
                          const RecordIcon = SAFETY_RECORD_ICONS[record.record_key] || ShieldCheck;
                          return <tr key={record.record_key}>
                            <td><span className="safety-dashboard-record"><RecordIcon size={15} aria-hidden="true" />{record.record_name}</span></td>
                            <td>{formatCount(record.daily_count)}</td>
                            <td>{formatCount(record.cumulative_count)}</td>
                          </tr>
                        })}
                      </tbody>
                    </table>
                  </div>
                </article>
              </div>
            </section>

            <section className="dashboard-table-section">
              <article className="chart-card chart-card-table table-card recently-released-card">
                <div className="chart-header">
                  <div>
                    <h2>Recently Resumed Equipment</h2>
                    <p className="chart-subtitle">Released from hold in the last 7 days</p>
                  </div>
                </div>
                <div className="recent-activities-wrap">
                  {recentlyReleasedEquipments.length === 0 ? (
                    <div className="recent-activities-empty">No equipment was released recently.</div>
                  ) : (
                    <div className="recent-activities-list">
                      {recentlyReleasedEquipments.map((equipment, index) => (
                        <button
                          type="button"
                          className="activity-item resumed-equipment-item"
                          key={`${equipment?.equipment_id || "released"}-${equipment?.released_at || index}`}
                          onClick={() => {
                            const params = new URLSearchParams({
                              source: "dashboard",
                              status: "all",
                              hold_status: "all",
                              highlightEquipmentId: String(equipment?.equipment_id || ""),
                              equipmentNumber: String(equipment?.equipment_number || ""),
                            });
                            navigate(`/equipments?${params.toString()}`);
                          }}
                        >
                          <span className={`activity-type-dot ${equipment?.started_after_release ? "is-started" : "is-waiting"}`} />
                          <span className="activity-main">
                            <span className="activity-title">{equipment?.equipment_number || "Equipment"}</span>
                            <span className="activity-subtitle">Released by {equipment?.released_by?.name || "Unknown"}{equipment?.process_name ? ` · ${equipment.process_name}` : ""}</span>
                            <span className="activity-time">
                              {equipment?.started_after_release
                                ? `Started by ${equipment?.started_by?.name || "team member"} · ${formatActivityDateTime(equipment?.started_at)}`
                                : `Released ${formatActivityDateTime(equipment?.released_at)}`}
                            </span>
                          </span>
                          
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            </section>

            <section className="dashboard-table-section">
              <div className="dashboard-dual-table-grid manpower-activities-grid">
                <article className="chart-card chart-card-table table-card">
                  <div className="chart-header">
                    <div>
                      <h2>Manpower Report</h2>
                      <p className="chart-subtitle">Today&apos;s deployed manpower by category</p>
                    </div>
                  </div>
                  <div className="simple-table-wrap manpower-table-wrap">
                    <table className="simple-report-table manpower-table manpower-dashboard-table">
                      <thead>
                        <tr><th>Work Category</th><th>Today</th></tr>
                      </thead>
                      <tbody>
                        {manpowerLoading && <tr><td colSpan="2" className="manpower-dashboard-message">Loading today&apos;s manpower…</td></tr>}
                        {!manpowerLoading && manpowerError && <tr><td colSpan="2" className="manpower-dashboard-message">{manpowerError}</td></tr>}
                        {!manpowerLoading && !manpowerError && manpowerCategories.map((category) => (
                          <tr key={category.category_key}>
                            <td>{category.category_name}</td>
                            <td>{formatCount(category.headcount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="manpower-total-row"><td>Total</td><td>{formatCount(totalManpower)}</td></tr>
                      </tfoot>
                    </table>
                  </div>
                </article>

                <article className="chart-card chart-card-table table-card">
                  <div className="chart-header">
                    <h2>Recent Activities</h2>
                  </div>
                  <div className="recent-activities-wrap">
                    {recentActivities.length === 0 ? (
                      <div className="recent-activities-empty">No recent activities found.</div>
                    ) : (
                      <div className="recent-activities-list">
                        {recentActivities.map((activity, index) => {
                          const activityType = String(activity?.activity_type || "").toLowerCase();
                          const dotColor =
                            ACTIVITY_TYPE_COLORS[activityType] || ACTIVITY_TYPE_COLORS.default;
                          const status = activity?.status || "Unknown";
                          const statusClass =
                            ACTIVITY_STATUS_COLORS[status] || "activity-status-default";

                          return (
                            <div
                              key={`${activity?.occurred_at || "activity"}-${index}`}
                              className="activity-item"
                            >
                              <span
                                className="activity-type-dot"
                                style={{ backgroundColor: dotColor }}
                                title={activityType || "activity"}
                              />
                              <div className="activity-main">
                                <p className="activity-title" title={activity?.title || "Untitled activity"}>{activity?.title || "Untitled activity"}</p>
                                <p className="activity-subtitle">{activity?.subtitle || "-"}</p>
                                <div className="activity-meta">
                                  {activity?.supervisor_name && (
                                    <>
                                      <span className="activity-supervisor" title={activity.supervisor_name}>
                                        <UserRound size={13} aria-hidden="true" />
                                        <span>{activity.supervisor_name}</span>
                                      </span>
                                      <span className="activity-meta-separator" aria-hidden="true">{"\u00B7"}</span>
                                    </>
                                  )}
                                  <time className="activity-time" dateTime={activity?.occurred_at || undefined}>
                                    {formatActivityDateTime(activity?.occurred_at)}
                                  </time>
                                </div>
                              </div>
                              <span className={`activity-status-badge ${statusClass}`} aria-label={`Status: ${status}`}>
                                {status === "Completed" && <CircleCheck size={16} strokeWidth={2.1} aria-hidden="true" />}
                                {status === "In Progress" && <Clock3 size={16} strokeWidth={2.1} aria-hidden="true" />}
                                {status}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </article>
              </div>
            </section>
          </div>
        )}
        {tbtHistoryOpen && (
          <div className="tbt-history-overlay" role="presentation" onMouseDown={() => setTbtHistoryOpen(false)}>
            <section className="tbt-history-modal" role="dialog" aria-modal="true" aria-labelledby="tbt-history-title" onMouseDown={(event) => event.stopPropagation()}>
              <header className="tbt-history-header">
                <div>
                  <h2 id="tbt-history-title"><History size={18} aria-hidden="true" />Tool Box Talk History</h2>
                  <p>Previously saved TBT sessions</p>
                  {tbtHistoryMonths.length > 0 && <label className="tbt-history-month-filter"><span>Month</span><select value={tbtHistoryMonth} onChange={(event) => loadTbtHistory({ month: event.target.value })} disabled={tbtHistoryLoading}>{tbtHistoryMonths.map((month) => <option key={month} value={month}>{formatTbtHistoryMonth(month)}</option>)}</select></label>}
                </div>
                <button type="button" className="tbt-history-close" onClick={() => setTbtHistoryOpen(false)} aria-label="Close TBT history"><X size={18} aria-hidden="true" /></button>
              </header>
              <div className="tbt-history-content" aria-busy={tbtHistoryLoading}>
                {tbtHistoryLoading && <p className="tbt-history-message">Loading TBT history…</p>}
                {!tbtHistoryLoading && tbtHistoryError && <p className="tbt-history-message">{tbtHistoryError}</p>}
                {!tbtHistoryLoading && !tbtHistoryError && tbtHistory.length === 0 && <p className="tbt-history-message">No Tool Box Talk sessions have been saved yet.</p>}
                {!tbtHistoryLoading && !tbtHistoryError && tbtHistory.map((session) => (
                  <section className="tbt-history-session" key={session.summary_date}>
                    <h3>{formatScurveTooltipDate(session.summary_date)}</h3>
                    <ul>
                      {(session.topics || []).map((topic) => <li key={topic.topic_key}>{topic.topic_name}</li>)}
                    </ul>
                  </section>
                ))}
                {!tbtHistoryLoading && !tbtHistoryError && tbtHistoryHasMore && <button type="button" className="tbt-history-load-more" onClick={() => loadTbtHistory({ month: tbtHistoryMonth, page: tbtHistoryPage + 1, append: true })} disabled={tbtHistoryLoadingMore}>{tbtHistoryLoadingMore ? "Loading…" : "Load more"}</button>}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default FieldTechnicianWelcome;
